import { useContext } from 'react';
import { FeatureFlagClientContext } from './FeatureFlagClientContext';
import type { FeatureGateName } from './gates';

/**
 * Read a feature gate by its typed name. Returns `true` only when a provider is
 * mounted AND it reports the gate enabled for the current user; every other
 * case (no provider, pre-init, fetch in flight, network failure, gate disabled)
 * returns `false`.
 *
 * That "default-false, fail-closed" contract is intentional: every call site
 * can treat the result as "should I expose this feature right now?" without
 * thinking about init races. The provider behind the context differs per
 * deployment (Statsig in commercial, the backend-config endpoint in GovCloud)
 * but the contract here does not.
 */
export function useFeatureGate(name: FeatureGateName): boolean {
  const client = useContext(FeatureFlagClientContext);
  return client?.checkGate(name) ?? false;
}

/**
 * Whether the gate values have resolved yet. `true` when no provider is mounted
 * (nothing to wait for). Route-level gating reads this so a hard refresh waits
 * for the async fetch instead of acting on a not-yet-loaded `false`.
 */
export function useFeatureFlagsReady(): boolean {
  const client = useContext(FeatureFlagClientContext);
  return client?.isReady() ?? true;
}
